<template>
  <BSkeletonWrapper
    :loading="loading"
  >
    <template #loading>
      <BCard>
        <BCardText>
          <div class="d-flex flex-column justify-content-center mb-3">
            <BSkeleton
              width="65%"
              height="23px"
              class="mb-2"
            />
            <BSkeleton
              width="80%"
              height="18px"
            />
          </div>
        </BCardText>

        <BCardText>
          <BRow class="gap-y-4">
            <BCol
              v-bind="childColumns"
              cols="12"
            >
              <BRow class="gap-y-2">
                <BCol
                  cols="12"
                  md="4"
                >
                  <BSkeleton
                    width="100%"
                    height="18px"
                  />
                </BCol>
                <BCol
                  cols="12"
                  md="8"
                  class="text-right"
                >
                  <BSkeleton
                    width="100%"
                    height="18px"
                  />
                </BCol>
              </BRow>
            </BCol>

            <BCol
              v-bind="childColumns"
              cols="12"
            >
              <BRow class="gap-y-2">
                <BCol
                  cols="12"
                  md="4"
                >
                  <BSkeleton
                    width="100%"
                    height="18px"
                  />
                </BCol>
                <BCol
                  cols="12"
                  md="8"
                  class="text-right"
                >
                  <BSkeleton
                    width="100%"
                    height="18px"
                  />
                </BCol>
              </BRow>
            </BCol>

            <BCol
              v-bind="childColumns"
              cols="12"
            >
              <BRow class="gap-y-2">
                <BCol
                  cols="12"
                  md="4"
                >
                  <BSkeleton
                    width="100%"
                    height="18px"
                  />
                </BCol>
                <BCol
                  cols="12"
                  md="8"
                  class="text-right"
                >
                  <BSkeleton
                    width="100%"
                    height="18px"
                  />
                </BCol>
              </BRow>
            </BCol>
          </BRow>
        </BCardText>
      </BCard>
    </template>

    <BCard
      body-class="p-2"
      class="rounded-lg"
    >
      <BCardText class="p-0">
        <div class="d-flex gap-3">
          <div class="d-flex flex-column flex-grow-1 gap-1">
            <RouterLink
              :to="{
                name: 'fso.to-dos.task-events',
                params: {
                  task: activity.id
                }
              }"
              style="font-size: 0.8rem;"
              class="font-weight-bold"
            >
              {{ activity?.activity_name }}
            </RouterLink>
            <div class="flex-grow-1 d-flex flex-column justify-content-end gap-1 mt-2">
              <div
                class="d-flex justify-content-between"
                style="font-size: 0.7rem;"
              >
                <div>
                  <b class="text-muted">{{ currentProgress.event }}</b> of <b class="text-muted">{{ totalProgress.event }}</b>
                </div>
                <div>
                  <b :class="`text-${eventVariant}`">{{ eventProgress.toFixed(2) }}%</b>
                </div>
              </div>
              <BProgress
                :max="totalProgress.event"
                height="0.5rem"
              >
                <BProgressBar
                  :value="currentProgress.event"
                  :variant="eventVariant"
                />
              </BProgress>
            </div>
          </div>
          <div class="d-flex align-items-center align-self-end">
            <slot name="footer" />
          </div>
        </div>
      </BCardText>
    </BCard>
  </BSkeletonWrapper>
</template>
<script>
import MxFormatter from '@/mixins/formatter'

export default {

  name: 'FSOTaskEventsTask',

  mixins: [
    MxFormatter
  ],

  props: {
    loading: {
      type: Boolean,
      default: () => false
    },
    childColumns: {
      type: Object,
      default: () => ({})
    },
    activity: {
      type: Object,
      required: true,
      default: () => ({
        id: '',
        activity_name: '',
        crop_name: '',
        total_buyers: 0
      })
    },
    totalProgress: {
      type: Object,
      required: true,
      default: () => ({
        event: 0,
        customer: 0,
        hectare: 0,
        cost: 0,
        sales: 0
      })
    },
    currentProgress: {
      type: Object,
      required: true,
      default: () => ({
        event: 0,
        customer: 0,
        hectare: 0,
        cost: 0,
        sales: 0
      })
    }
  },

  computed: {
    eventProgress () {
      const events = this.currentProgress.event / this.totalProgress.event
      if (isNaN(events) || !isFinite(events)) {
        return 0
      }

      return events * 100
    },

    eventVariant () {
      if (this.eventProgress >= 75) {
        return 'success'
      } else if (this.eventProgress >= 31) {
        return 'warning'
      } else if (this.eventProgress >= 0.01) {
        return 'danger'
      } else {
        return 'dark'
      }
    },

    customerProgress () {
      const customers = this.currentProgress.customer / this.totalProgress.customer
      if (isNaN(customers) || !isFinite(customers)) {
        return 0
      }

      return customers * 100
    },

    customerVariant () {
      if (this.customerProgress >= 75) {
        return 'success'
      } else if (this.customerProgress >= 31) {
        return 'warning'
      } else if (this.customerProgress >= 0.01) {
        return 'danger'
      } else {
        return 'dark'
      }
    },

    hectareProgress () {
      const hectares = this.currentProgress.hectare / this.totalProgress.hectare
      if (isNaN(hectares) || !isFinite(hectares)) {
        return 0
      }

      return hectares * 100
    },

    hectareVariant () {
      if (this.hectareProgress >= 75) {
        return 'success'
      } else if (this.hectareProgress >= 31) {
        return 'warning'
      } else if (this.hectareProgress >= 0.01) {
        return 'danger'
      } else {
        return 'dark'
      }
    },

    conversionRate () {
      const rates = this.activity.total_buyers / this.currentProgress.customer
      if (isNaN(rates) || !isFinite(rates)) {
        return 0
      }

      return rates * 100
    },

    conversionRateVariant () {
      if (this.conversionRate >= 75) {
        return 'success'
      } else if (this.conversionRate >= 31) {
        return 'warning'
      } else if (this.conversionRate >= 0.01) {
        return 'danger'
      } else {
        return 'dark'
      }
    },

    costProgress () {
      const costs = this.currentProgress.cost / this.totalProgress.cost
      if (isNaN(costs) || !isFinite(costs)) {
        return 0
      }

      return costs * 100
    },

    costVariant () {
      if (this.costProgress >= 75) {
        return 'success'
      } else if (this.costProgress >= 31) {
        return 'warning'
      } else if (this.costProgress >= 0.01) {
        return 'danger'
      } else {
        return 'dark'
      }
    },

    salesProgress () {
      const sales = this.currentProgress.sales / this.totalProgress.sales
      if (isNaN(sales) || !isFinite(sales)) {
        return 0
      }

      return sales * 100
    },

    salesVariant () {
      if (this.salesProgress >= 75) {
        return 'success'
      } else if (this.salesProgress >= 31) {
        return 'warning'
      } else if (this.salesProgress >= 0.01) {
        return 'danger'
      } else {
        return 'dark'
      }
    }

  },

  methods: {
    variant () {
      // todo: create officer
    }
  }
}
</script>
